<template>
    <SiteFooter :hasLegalText="!!legalFooter" :hideNavigation="hideNavigation" :largeLogoContainer="false">
        <template v-slot:primary-navigation>
            <li
                v-for="(menuItem, index) in primaryMenu"
                :key="index"
                :class="`col nav-list__item ${menuMisc.activeMenuItemParent == menuItem.name && 'is-active'}
          ${!menuMisc.showLegalTextLabel && index != primaryMenu.length - 1 ? 'last' : ''}`"
            >
                <a class="nav-list__link" :href="menuItem.url">
                    {{ menuItem.name }}
                    <span
                        v-if="menuItem.children && menuItem.children.length"
                        class="nav-list__icon icon icon-caret-down"
                        aria-hidden="true"
                    ></span>
                </a>

                <div class="menu" v-if="menuItem.children && menuItem.children.length">
                    <ul class="menu__list">
                        <li
                            v-for="(menuItem, index) in menuItem.children"
                            :key="index"
                            :class="`menu__item ${menuMisc.activeMenuItemChild == menuItem.name && 'is-active'}`"
                        >
                            <a class="menu__link" :href="menuItem.url">{{ menuItem.name }}</a>
                        </li>
                    </ul>
                </div>
            </li>
        </template>

        <template v-slot:powered-by>
            <div v-if="themeSetting('quint_group_footer_logo')">
                <div class="logo">
                    <a href="https://www.quint.co.uk/">
                        <img
                            class="footer-logo"
                            :src="asset(`/v2/images/${themeSetting('quint_group_footer_logo')}`)"
                            alt="Part of Quint Group"
                        />
                    </a>
                    <div class="logo__text">
                        <div :class="['line1', logoText ? 'line_white' : 'line_black']">part of</div>
                        <div :class="['line2', logoText ? 'line_white' : 'line_black']">Quint Group</div>
                    </div>
                </div>
            </div>

            <img
                class="footer-logo"
                v-if="themeSetting('monevo_footer_logo') && !themeSetting('quint_group_footer_logo')"
                :src="asset(`/img/logos/${themeSetting('monevo_footer_logo')}`)"
                width="100"
                alt="Monevo"
            />
        </template>

        <template v-slot:secondary-navigation>
            <ul class="nav-list d-flex flex-column flex-lg-row" v-if="secondaryMenu">
                <li class="menu-item footer-copyright-item">
                    <span>{{ $t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
                </li>
                <li class="menu-item" v-for="(menuItem, index) in secondaryMenu" :key="index">
                    <ConditionalLink
                        type="external"
                        link="#cookies"
                        target="_parent"
                        classes="t-small ot-sdk-show-settings"
                        v-if="menuItem.name === 'Cookie Preference' && hasOneTrustCookieToken"
                    >
                        {{ menuItem.name }}
                    </ConditionalLink>

                    <ConditionalLink
                        :type="menuItem.type ?? 'internal'"
                        :link="menuItem.url"
                        target="_parent'"
                        classes="t-small"
                        v-else-if="menuItem.url"
                    >
                        {{ menuItem.name }}
                    </ConditionalLink>
                </li>
            </ul>
        </template>

        <template v-slot:legal v-if="legalFooter && showLegalFooter">
            <div class="container">
                <div class="row">
                    <div class="col">
                        <div class="legal-footer t-small">
                            <div v-html="legalFooter"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </SiteFooter>
</template>

<script>
import { useStore } from 'vuex'
export default {
    props: {
        hideNavigation: Boolean,
        isRatesTable: Boolean,
        showLegalFooter: {
            type: Boolean,
            default: true,
        },
    },
    setup() {
        const store = useStore()
        return {
            store,
        }
    },
    computed: {
        territory: function () {
            return process.env.TERRITORY
        },
        legalFooter: function () {
            return this.store.state.global.data.legal_footer || false
        },
        menu: function () {
            return this.store.state.global.data.menu || {}
        },
        menuMisc: function () {
            return this.menu.misc || false
        },
        primaryMenu: function () {
            return this.menu.primary || false
        },
        secondaryMenu: function () {
            return this.menu.secondary || false
        },
        hasOneTrustCookieToken: function () {
            return (window.ONE_TRUST_COOKIE_TOKEN && true) || false
        },
        logoText: function () {
            return this.themeSetting('monevo_footer_logo_switch') === '0'
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';
@import '~scss/settings/variables';
@import '~scss/mixins/rem';
.logo {
    display: flex;
    justify-content: center;
    margin: 1.5em 1em 0;
    position: relative;
    text-align: left;

    @media all and (min-width: $bp-medium) {
        justify-content: flex-start;
    }

    &__img {
        margin: 0;
        width: 40px;
    }

    &__text {
        margin: -4px 0 0 5px;
        font-size: 0.8rem;
        .line1 {
            font-size: 0.875em;
            white-space: nowrap;
        }
        .line2 {
            white-space: nowrap;
        }

        .line_white {
            color: white;
        }

        .line_black {
            color: black;
        }
        @media screen and (max-width: $bp-medium) {
            margin: 0 0 0 5px;
            .line1 {
                font-size: 11px;
            }
            .line2 {
                font-size: 13px;
            }
        }
    }
}
.footer-logo {
    max-height: 35px;
    margin-left: 10px;
}
.footer-logo:nth-of-type(2) {
    margin-top: 15px;
    @media screen and (min-width: $bp-medium) {
        margin-top: 0;
    }
    @media screen and (max-width: $bp-medium) {
        margin-top: 44px;
    }
}
.nav-list__link {
    font-weight: bold;
    margin-bottom: 16px;
    white-space: nowrap;
    @media screen and (min-width: $bp-medium) {
        margin-bottom: 0;
    }
}
.legal-footer {
    background-color: var(--footer-legal-background-color);
    border-radius: 4px;
    color: var(--footer-legal-text-color);
    padding: 25px 25px 25px 25px;
    text-align: justify;

    @media all and (max-width: $bp-small) {
        padding: rem(12.6px);
    }
}

.nav-list {
    display: flex;
    flex-wrap: wrap;
    font-size: 1em;
    justify-content: flex-end;
    list-style-type: none;
    margin: 0;
    padding: 0;

    @media all and (max-width: $bp-large) {
        justify-content: space-evenly;
        align-content: space-between;
        display: grid !important;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
}

.menu-item {
    margin: 0.9375em 0 0 1em;
    white-space: nowrap;

    @media all and (max-width: $bp-medium) {
        margin: 1em 1em 0;
    }
}

.footer-copyright-item {
    margin-right: auto;

    span {
        color: var(--footer-secondary-link-color);
    }

    @media all and (max-width: $bp-large) {
        display: none;
    }
}
</style>
